<script setup>
/**
 * Desktop Canvas Component
 * Device-aware grid system:
 * - Phone: 6 columns (shows only cols 0-5)
 * - Tablet: 11 columns (shows only cols 0-10)
 * - PC: Dynamic columns with visual zones showing phone/tablet areas
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import DesktopIcon from './DesktopIcon.vue'
import {
  GRID_CONFIG,
  calculateGridDimensions,
  calculateSnapPosition,
  findNearestEmptyCell,
  getCellPosition,
  getMaxColsForDevice,
} from '@/core/config/desktopShortcuts'

const props = defineProps({
  shortcuts: {
    type: Array,
    default: () => [],
  },
  showZones: {
    type: Boolean,
    default: true,
  },
  wallpaperStyle: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['shortcut-move', 'shortcut-remove', 'shortcut-drag-start'])

// Canvas ref for dimensions
const canvasRef = ref(null)
const canvasWidth = ref(0)
const canvasHeight = ref(0)

// Dragging state for grid overlay
const isDraggingAny = ref(false)
const currentDragId = ref(null)
const previewCell = ref(null)

// Resize debounce
let resizeTimeout = null

function updateCanvasSize() {
  if (canvasRef.value) {
    canvasWidth.value = canvasRef.value.offsetWidth
    canvasHeight.value = canvasRef.value.offsetHeight
  }
}

function handleResize() {
  if (resizeTimeout) clearTimeout(resizeTimeout)
  resizeTimeout = setTimeout(() => {
    updateCanvasSize()
  }, 150)
}

onMounted(() => {
  updateCanvasSize()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeTimeout) clearTimeout(resizeTimeout)
})

// Calculate dynamic grid dimensions
const gridDimensions = computed(() => {
  return calculateGridDimensions(canvasWidth.value, canvasHeight.value)
})

const gridCols = computed(() => gridDimensions.value.cols)
const gridRows = computed(() => gridDimensions.value.rows)
const cellWidth = computed(() => gridDimensions.value.cellWidth)
const cellHeight = computed(() => gridDimensions.value.cellHeight)
const iconSize = computed(() => gridDimensions.value.iconSize)
const deviceType = computed(() => gridDimensions.value.deviceType)

// Check if we're on PC (to show visual zones)
const isPC = computed(() => deviceType.value === 'pc')

// Get max columns for current device
const maxColsForDevice = computed(() => {
  return getMaxColsForDevice(deviceType.value, gridCols.value)
})

// Filter shortcuts to only show those visible on current device
const visibleShortcuts = computed(() => {
  return props.shortcuts.filter((shortcut) => {
    const col = shortcut.gridCol
    if (col === undefined) return false
    return col < maxColsForDevice.value
  })
})

// Track occupied cells (excluding currently dragged icon)
const occupiedCells = computed(() => {
  const occupied = new Set()
  props.shortcuts.forEach((shortcut) => {
    if (shortcut.id === currentDragId.value) return
    if (shortcut.gridCol !== undefined && shortcut.gridRow !== undefined) {
      occupied.add(`${shortcut.gridCol},${shortcut.gridRow}`)
    }
  })
  return occupied
})

// Generate grid cells for overlay (with zone info for PC)
const gridCells = computed(() => {
  const cells = []
  const { GAP, PADDING, PHONE_COLS, TABLET_COLS } = GRID_CONFIG

  for (let row = 0; row < gridRows.value; row++) {
    for (let col = 0; col < gridCols.value; col++) {
      // Calculate cell position (RTL - from right edge)
      const x = canvasWidth.value - PADDING - cellWidth.value - col * (cellWidth.value + GAP)
      const y = PADDING + row * (cellHeight.value + GAP)

      // Determine zone (for PC visual indicators)
      let zone = 'pc'
      if (col < PHONE_COLS) {
        zone = 'phone'
      } else if (col < TABLET_COLS) {
        zone = 'tablet'
      }

      cells.push({
        col,
        row,
        x,
        y,
        width: cellWidth.value,
        height: cellHeight.value,
        zone,
        isOccupied: occupiedCells.value.has(`${col},${row}`),
        isPreview: previewCell.value?.col === col && previewCell.value?.row === row,
      })
    }
  }
  return cells
})

// Calculate zone backgrounds for PC view
const phoneZoneStyle = computed(() => {
  if (!isPC.value || canvasWidth.value === 0) return null

  const { GAP, PADDING, PHONE_COLS } = GRID_CONFIG
  const zoneWidth = PHONE_COLS * cellWidth.value + (PHONE_COLS - 1) * GAP

  return {
    right: `${PADDING}px`,
    top: '0',
    bottom: '0',
    width: `${zoneWidth}px`,
  }
})

const tabletZoneStyle = computed(() => {
  if (!isPC.value || canvasWidth.value === 0) return null

  const { GAP, PADDING, PHONE_COLS, TABLET_COLS } = GRID_CONFIG
  const phoneZoneWidth = PHONE_COLS * cellWidth.value + (PHONE_COLS - 1) * GAP
  const tabletOnlyCols = TABLET_COLS - PHONE_COLS
  const zoneWidth = tabletOnlyCols * cellWidth.value + tabletOnlyCols * GAP

  return {
    right: `${PADDING + phoneZoneWidth + GAP}px`,
    top: '0',
    bottom: '0',
    width: `${zoneWidth}px`,
  }
})

// Track icon refs for position syncing
const iconRefs = ref({})

function setIconRef(id, el) {
  if (el) {
    iconRefs.value[id] = el
  }
}

// Sync icon positions when shortcuts change
watch(
  () => props.shortcuts,
  (newShortcuts) => {
    newShortcuts.forEach((shortcut) => {
      const iconRef = iconRefs.value[shortcut.id]
      if (iconRef && iconRef.syncPosition) {
        iconRef.syncPosition()
      }
    })
  },
  { deep: true }
)

// Drag handlers
function handleIconDragStart(shortcutId) {
  isDraggingAny.value = true
  currentDragId.value = shortcutId
  emit('shortcut-drag-start', shortcutId)
}

function handleIconDragMove(shortcutId, position) {
  const snap = calculateSnapPosition(position.x, position.y, canvasWidth.value, canvasHeight.value)
  const empty = findNearestEmptyCell(
    snap.col,
    snap.row,
    occupiedCells.value,
    gridCols.value,
    gridRows.value
  )
  previewCell.value = empty
}

function handleIconDragEnd(shortcutId, position) {
  isDraggingAny.value = false
  currentDragId.value = null
  previewCell.value = null

  const snap = calculateSnapPosition(position.x, position.y, canvasWidth.value, canvasHeight.value)
  const finalCell = findNearestEmptyCell(
    snap.col,
    snap.row,
    occupiedCells.value,
    gridCols.value,
    gridRows.value
  )
  const finalPos = getCellPosition(
    finalCell.col,
    finalCell.row,
    canvasWidth.value,
    canvasHeight.value
  )

  emit('shortcut-move', shortcutId, {
    x: finalPos.x,
    y: finalPos.y,
    gridCol: finalCell.col,
    gridRow: finalCell.row,
  })
}

function handleIconRemove(shortcutId) {
  emit('shortcut-remove', shortcutId)
}
</script>

<template>
  <div ref="canvasRef" class="desktop-canvas" :class="{ 'dragging-mode': isDraggingAny }">
    <!-- Visual zones for PC (shows phone/tablet areas) -->
    <Transition name="zone-fade">
      <div
        v-if="isPC && showZones && phoneZoneStyle"
        class="zone phone-zone"
        :style="phoneZoneStyle"
      >
        <span class="zone-label">📱 פלאפון</span>
      </div>
    </Transition>
    <Transition name="zone-fade">
      <div
        v-if="isPC && showZones && tabletZoneStyle"
        class="zone tablet-zone"
        :style="tabletZoneStyle"
      >
        <span class="zone-label">📟 טאבלט</span>
      </div>
    </Transition>

    <!-- Grid overlay (visible while dragging) -->
    <div v-show="isDraggingAny" class="grid-overlay">
      <div
        v-for="cell in gridCells"
        :key="`${cell.col}-${cell.row}`"
        class="grid-cell"
        :class="{
          occupied: cell.isOccupied,
          preview: cell.isPreview,
          [`zone-${cell.zone}`]: isPC && showZones,
        }"
        :style="{
          left: `${cell.x}px`,
          top: `${cell.y}px`,
          width: `${cell.width}px`,
          height: `${cell.height}px`,
        }"
      ></div>
    </div>

    <!-- Icons (filtered by device) -->
    <DesktopIcon
      v-for="shortcut in visibleShortcuts"
      :key="shortcut.id"
      :ref="(el) => setIconRef(shortcut.id, el)"
      :shortcut="shortcut"
      :icon-size="iconSize"
      :cell-width="cellWidth"
      :cell-height="cellHeight"
      :canvas-width="canvasWidth"
      :canvas-height="canvasHeight"
      @drag-start="handleIconDragStart"
      @drag-move="handleIconDragMove"
      @drag-end="handleIconDragEnd"
      @remove="handleIconRemove"
    />

    <!-- Empty state -->
    <div v-if="visibleShortcuts.length === 0" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
        </svg>
      </div>
      <p class="empty-title">שולחן העבודה ריק</p>
      <p class="empty-hint">לחץ על "הוסף קיצור" כדי להוסיף קיצורי דרך</p>
    </div>

    <!-- Background pattern / wallpaper -->
    <div v-if="wallpaperStyle" class="canvas-wallpaper" :style="wallpaperStyle"></div>
    <div v-else class="canvas-pattern"></div>
  </div>
</template>

<style scoped>
.desktop-canvas {
  position: relative;
  flex: 1;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
  overflow: hidden;
}

.canvas-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.04) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.04) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.canvas-wallpaper {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

/* Visual Zones for PC */
.zone {
  position: absolute;
  pointer-events: none;
  z-index: 0;
}

.phone-zone {
  background: rgba(34, 197, 94, 0.08);
  border-left: 2px dashed rgba(34, 197, 94, 0.4);
}

.tablet-zone {
  background: rgba(168, 85, 247, 0.06);
  border-left: 2px dashed rgba(168, 85, 247, 0.4);
}

.zone-label {
  position: absolute;
  bottom: 10px;
  right: 10px;
  font-size: 0.7rem;
  color: #64748b;
  background: rgba(255, 255, 255, 0.8);
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

/* Zone transitions */
.zone-fade-enter-active,
.zone-fade-leave-active {
  transition: opacity 0.25s ease;
}

.zone-fade-enter-from,
.zone-fade-leave-to {
  opacity: 0;
}

/* Grid Overlay */
.grid-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.grid-cell {
  position: absolute;
  border: 1px dashed rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  transition: all 0.15s ease;
}

/* Zone-specific cell colors on PC while dragging */
.grid-cell.zone-phone {
  border-color: rgba(34, 197, 94, 0.5);
}

.grid-cell.zone-tablet {
  border-color: rgba(168, 85, 247, 0.4);
}

.grid-cell.zone-pc {
  border-color: rgba(59, 130, 246, 0.3);
}

.grid-cell.occupied {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.4);
}

.grid-cell.preview {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.6);
  border-style: solid;
  border-width: 2px;
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.3);
}

/* Empty state */
.empty-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 1;
  pointer-events: none;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1rem;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
}

.empty-title {
  color: #475569;
  font-size: 1.1rem;
  margin: 0 0 0.5rem 0;
}

.empty-hint {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0;
}
</style>
